import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import Layout from '@/components/layout/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { CalendarIcon, Download, RefreshCw, Database } from 'lucide-react';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import ChipChipSalesChart from '@/components/chipchip/ChipChipSalesChart';

interface ChipChipSalesData {
  id: string;
  product_name: string;
  date: string;
  order_type: string;
  price: number;
  num_order: number;
  quantity: number;
  sync_date: string;
}

const ChipChipSalesData = () => {
  const [startDate, setStartDate] = useState<Date>(new Date('2024-05-01'));
  const [endDate, setEndDate] = useState<Date>(new Date());
  const [selectedProduct, setSelectedProduct] = useState<string>('all');
  const [isLoading, setIsLoading] = useState(false);
  const [syncProgress, setSyncProgress] = useState(0);
  const [syncStatus, setSyncStatus] = useState<string>('');
  const [isSyncing, setIsSyncing] = useState(false);
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      const el = document.querySelector(location.hash);
      if (el) setTimeout(() => el.scrollIntoView({ behavior: 'smooth', block: 'start' }), 300);
    }
  }, [location.hash]);


  // Fetch ChipChip sales data from database (filtered by date range)
  const { data: salesData, isLoading: isDataLoading } = useQuery({
    queryKey: ['chipchip-sales-data', startDate, endDate],
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from('chipchip_sales_data')
        .select('*')
        .gte('date', format(startDate, 'yyyy-MM-dd'))
        .lte('date', format(endDate, 'yyyy-MM-dd'))
        .order('date', { ascending: false })
        .limit(10000);

      if (error) throw error;
      return data as ChipChipSalesData[];
    },
  });

  // Fetch ChipChip daily revenue fees (packaging + delivery) for current range
  const { data: feeRows } = useQuery({
    queryKey: ['chipchip-daily-revenue-fees', startDate, endDate],
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from('chipchip_daily_revenue_fees')
        .select('date, packaging_fee_total, delivery_charge_total, combined_total')
        .gte('date', format(startDate, 'yyyy-MM-dd'))
        .lte('date', format(endDate, 'yyyy-MM-dd'))
        .order('date', { ascending: false })
        .limit(10000);
      if (error) throw error;
      return data as Array<{ date: string; packaging_fee_total: number; delivery_charge_total: number; combined_total: number }>;
    },
  });

  const feesSummary = feeRows ? {
    packaging: feeRows.reduce((s, r) => s + Number(r.packaging_fee_total || 0), 0),
    delivery: feeRows.reduce((s, r) => s + Number(r.delivery_charge_total || 0), 0),
    combined: feeRows.reduce((s, r) => s + Number(r.combined_total || 0), 0),
    days: feeRows.length,
  } : null;

  // Coverage of fees data already in DB (min/max date + total days synced)
  const { data: feesCoverage } = useQuery({
    queryKey: ['chipchip-fees-coverage'],
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from('chipchip_daily_revenue_fees')
        .select('date, synced_at')
        .order('date', { ascending: true })
        .limit(10000);
      if (error) throw error;
      const rows = (data || []) as Array<{ date: string; synced_at: string }>;
      if (!rows.length) return null;
      const sortedSync = [...rows].sort((a, b) => (a.synced_at < b.synced_at ? 1 : -1));
      return {
        minDate: rows[0].date,
        maxDate: rows[rows.length - 1].date,
        totalDays: rows.length,
        lastSyncedAt: sortedSync[0]?.synced_at,
      };
    },
  });

  // Dedicated date range for the fees sync action (defaults to last 7 days)
  const [feesStartDate, setFeesStartDate] = useState<Date>(() => {
    const d = new Date();
    d.setDate(d.getDate() - 7);
    return d;
  });
  const [feesEndDate, setFeesEndDate] = useState<Date>(new Date());

  // Dedicated date range for the accepted-daily sync (orders / qty / GMV)
  const [acceptedStartDate, setAcceptedStartDate] = useState<Date>(() => {
    const d = new Date();
    d.setDate(d.getDate() - 7);
    return d;
  });
  const [acceptedEndDate, setAcceptedEndDate] = useState<Date>(new Date());
  const [isAcceptedSyncing, setIsAcceptedSyncing] = useState(false);

  // Fetch accepted-daily totals for the main page date range
  const { data: acceptedRows } = useQuery({
    queryKey: ['chipchip-daily-accepted-totals', startDate, endDate],
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from('chipchip_daily_accepted_totals')
        .select('date, total_orders, total_quantity, total_gmv')
        .gte('date', format(startDate, 'yyyy-MM-dd'))
        .lte('date', format(endDate, 'yyyy-MM-dd'))
        .order('date', { ascending: false })
        .limit(10000);
      if (error) throw error;
      return data as Array<{ date: string; total_orders: number; total_quantity: number; total_gmv: number }>;
    },
  });

  const acceptedSummary = acceptedRows ? {
    orders: acceptedRows.reduce((s, r) => s + Number(r.total_orders || 0), 0),
    quantity: acceptedRows.reduce((s, r) => s + Number(r.total_quantity || 0), 0),
    gmv: acceptedRows.reduce((s, r) => s + Number(r.total_gmv || 0), 0),
    days: acceptedRows.length,
  } : null;

  // Coverage of accepted-daily already in DB
  const { data: acceptedCoverage } = useQuery({
    queryKey: ['chipchip-accepted-coverage'],
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from('chipchip_daily_accepted_totals')
        .select('date, synced_at')
        .order('date', { ascending: true })
        .limit(10000);
      if (error) throw error;
      const rows = (data || []) as Array<{ date: string; synced_at: string }>;
      if (!rows.length) return null;
      const sortedSync = [...rows].sort((a, b) => (a.synced_at < b.synced_at ? 1 : -1));
      return {
        minDate: rows[0].date,
        maxDate: rows[rows.length - 1].date,
        totalDays: rows.length,
        lastSyncedAt: sortedSync[0]?.synced_at,
      };
    },
  });

  const handleSyncAccepted = async () => {
    if (acceptedStartDate > acceptedEndDate) {
      toast({ title: 'Invalid Range', description: 'Start date must be before end date', variant: 'destructive' });
      return;
    }
    setIsAcceptedSyncing(true);
    try {
      const { data, error } = await supabase.functions.invoke('chipchip-accepted-daily-sync', {
        body: {
          startDate: format(acceptedStartDate, 'yyyy-MM-dd'),
          endDate: format(acceptedEndDate, 'yyyy-MM-dd'),
        },
      });
      if (error) throw error;
      toast({
        title: 'Accepted Totals Synced',
        description: `Synced ${data?.days ?? 0} day(s): ${(data?.totals?.orders ?? 0).toLocaleString()} orders, GMV ${(data?.totals?.gmv ?? 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`,
      });
      queryClient.invalidateQueries({ queryKey: ['chipchip-daily-accepted-totals'] });
      queryClient.invalidateQueries({ queryKey: ['chipchip-accepted-coverage'] });
    } catch (err: any) {
      toast({ title: 'Accepted Sync Failed', description: err.message, variant: 'destructive' });
    } finally {
      setIsAcceptedSyncing(false);
    }
  };

  // Fetch all-time statistics using database function
  const { data: allTimeStats } = useQuery({
    queryKey: ['chipchip-all-time-stats'],
    queryFn: async () => {
      try {
        // Get total record count
        const { count, error: countError } = await (supabase as any)
          .from('chipchip_sales_data')
          .select('*', { count: 'exact', head: true });

        if (countError) throw countError;

        // Use the database function to get aggregated statistics
        const { data: aggregateData, error: aggregateError } = await (supabase as any)
          .rpc('get_chipchip_aggregate_stats');

        if (aggregateError) {
          console.error('Aggregate function failed:', aggregateError);
          return {
            totalRecords: count || 0,
            totalOrders: 0,
            totalQuantity: 0,
            uniqueProducts: 0,
          };
        }

        const result = aggregateData?.[0];
        
        console.log('All-time stats from database:', { 
          totalRecords: count, 
          totalOrders: result?.total_orders,
          totalQuantity: result?.total_quantity,
          uniqueProducts: result?.unique_products
        });

        return {
          totalRecords: count || 0,
          totalOrders: Number(result?.total_orders) || 0,
          totalQuantity: Number(result?.total_quantity) || 0,
          uniqueProducts: Number(result?.unique_products) || 0,
        };
      } catch (error) {
        console.error('All-time stats query failed:', error);
        return {
          totalRecords: 0,
          totalOrders: 0,
          totalQuantity: 0,
          uniqueProducts: 0,
        };
      }
    },
  });


  const handleSync = async () => {
    setIsSyncing(true);
    setSyncProgress(10);
    setSyncStatus('Preparing sync...');
    
    try {
      setSyncProgress(30);
      setSyncStatus('Fetching products...');
      
      const { data: existingProducts } = await (supabase as any)
        .from('chipchip_sales_data')
        .select('product_name')
        .limit(1000);

      const uniqueProducts = Array.from(
        new Set(existingProducts?.map(p => p.product_name) || [
          'Red Onion A', 'Tomato A', 'Potato', 'Carrot', 'Chili Green'
        ])
      );

      setSyncProgress(50);
      setSyncStatus(`Syncing ${uniqueProducts.length} products...`);

      const { data, error } = await supabase.functions.invoke('chipchip-sales-sync', {
        body: {
          products: uniqueProducts,
          startDate: format(startDate, 'yyyy-MM-dd'),
          endDate: format(endDate, 'yyyy-MM-dd'),
          saveToDatabase: true,
        },
      });

      if (error) throw error;

      setSyncProgress(90);
      setSyncStatus('Finalizing...');

      const totalRecords = data?.data?.length || 0;
      
      setSyncProgress(100);
      setSyncStatus('Sync completed!');

      setTimeout(() => {
        setIsSyncing(false);
        setSyncProgress(0);
        setSyncStatus('');
      }, 1000);

      toast({
        title: 'Sync Completed',
        description: `Successfully synced ${totalRecords} records from ChipChip`,
      });
      queryClient.invalidateQueries({ queryKey: ['chipchip-sales-data'] });
      queryClient.invalidateQueries({ queryKey: ['chipchip-all-time-stats'] });
      queryClient.invalidateQueries({ queryKey: ['chipchip-daily-revenue-fees'] });
    } catch (error) {
      setIsSyncing(false);
      setSyncProgress(0);
      setSyncStatus('');
      toast({
        title: 'Sync Failed',
        description: error.message,
        variant: 'destructive',
      });
    }
  };

  const handleFullSync = async () => {
    setIsSyncing(true);
    setSyncProgress(10);
    setSyncStatus('Preparing full sync...');
    
    try {
      setSyncProgress(30);
      setSyncStatus('Initiating full sync from May 2024...');

      const { data, error } = await supabase.functions.invoke('chipchip-sales-sync', {
        body: {
          startDate: format(new Date('2024-05-01'), 'yyyy-MM-dd'),
          endDate: format(new Date(), 'yyyy-MM-dd'),
          saveToDatabase: true,
          fullSync: true,
        },
      });

      if (error) throw error;

      setSyncProgress(90);
      setSyncStatus('Finalizing full sync...');
      
      const totalRecords = data?.synced || 0;
      
      setSyncProgress(100);
      setSyncStatus('Full sync completed!');

      setTimeout(() => {
        setIsSyncing(false);
        setSyncProgress(0);
        setSyncStatus('');
      }, 1000);

      toast({
        title: 'Full Sync Completed',
        description: `Successfully synced ${totalRecords} records from ChipChip`,
      });
      queryClient.invalidateQueries({ queryKey: ['chipchip-sales-data'] });
      queryClient.invalidateQueries({ queryKey: ['chipchip-all-time-stats'] });
      queryClient.invalidateQueries({ queryKey: ['chipchip-daily-revenue-fees'] });
    } catch (error) {
      setIsSyncing(false);
      setSyncProgress(0);
      setSyncStatus('');
      toast({
        title: 'Full Sync Failed',
        description: error.message,
        variant: 'destructive',
      });
    }
  };

  // Manual sync of ONLY the other-revenue fees (packaging + delivery) for the current range
  const [isFeesSyncing, setIsFeesSyncing] = useState(false);
  const handleSyncFees = async () => {
    if (feesStartDate > feesEndDate) {
      toast({ title: 'Invalid Range', description: 'Start date must be before end date', variant: 'destructive' });
      return;
    }
    setIsFeesSyncing(true);
    try {
      const { data, error } = await supabase.functions.invoke('chipchip-sales-sync', {
        body: {
          startDate: format(feesStartDate, 'yyyy-MM-dd'),
          endDate: format(feesEndDate, 'yyyy-MM-dd'),
          feesOnly: true,
        },
      });
      if (error) throw error;
      toast({
        title: 'Fees Synced',
        description: `Synced ${data?.fees_synced ?? 0} day(s) of packaging + delivery fees (${format(feesStartDate, 'MMM dd')} - ${format(feesEndDate, 'MMM dd, yyyy')})`,
      });
      queryClient.invalidateQueries({ queryKey: ['chipchip-daily-revenue-fees'] });
      queryClient.invalidateQueries({ queryKey: ['chipchip-fees-coverage'] });
    } catch (err: any) {
      toast({ title: 'Fees Sync Failed', description: err.message, variant: 'destructive' });
    } finally {
      setIsFeesSyncing(false);
    }
  };

  const exportToCSV = () => {
    if (!salesData?.length) return;

    const csvContent = [
      ['Product Name', 'Date', 'Order Type', 'Price', 'Number of Orders', 'Quantity', 'Sync Date'],
      ...salesData.map(row => [
        row.product_name,
        row.date,
        row.order_type,
        row.price,
        row.num_order,
        row.quantity,
        row.sync_date
      ])
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `chipchip-sales-data-${format(new Date(), 'yyyy-MM-dd')}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const filteredSummary = salesData ? {
    totalRecords: salesData.length,
    totalOrders: salesData.reduce((sum, item) => sum + item.num_order, 0),
    totalQuantity: salesData.reduce((sum, item) => sum + item.quantity, 0),
    uniqueProducts: new Set(salesData.map(item => item.product_name)).size,
  } : null;

  // Get unique products for filtering
  const availableProducts = salesData ? 
    Array.from(new Set(salesData.map(item => item.product_name))).sort() : [];

  return (
    <Layout>
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">ChipChip Sales Data</h1>
          <p className="text-muted-foreground">
            Sync and manage sales data from ChipChip platform
          </p>
        </div>
        <div className="flex gap-2">
          <Button onClick={exportToCSV} variant="outline" disabled={!salesData?.length}>
            <Download className="mr-2 h-4 w-4" />
            Export CSV
          </Button>
        </div>
      </div>

      {/* All-Time Summary Cards */}
      {allTimeStats && (
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <h2 className="text-lg font-semibold">All-Time Statistics</h2>
            <Badge variant="secondary">Database Total</Badge>
          </div>
          <div className="grid gap-4 md:grid-cols-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Total Records</CardTitle>
                <Database className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{allTimeStats.totalRecords.toLocaleString()}</div>
                <p className="text-xs text-muted-foreground">All-time database records</p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Total Orders</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{allTimeStats.totalOrders.toLocaleString()}</div>
                <p className="text-xs text-muted-foreground">All-time order count</p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Total Quantity</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{allTimeStats.totalQuantity.toLocaleString()}</div>
                <p className="text-xs text-muted-foreground">All-time quantity sold</p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Unique Products</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{allTimeStats.uniqueProducts}</div>
                <p className="text-xs text-muted-foreground">Unique product types</p>
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {/* Filtered Summary Cards */}
      {filteredSummary && (
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <h2 className="text-lg font-semibold">Filtered Results</h2>
            <Badge variant="outline">
              {format(startDate, 'MMM dd, yyyy')} - {format(endDate, 'MMM dd, yyyy')}
            </Badge>
          </div>
          <div className="grid gap-4 md:grid-cols-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Records</CardTitle>
                <Database className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{filteredSummary.totalRecords.toLocaleString()}</div>
                <p className="text-xs text-muted-foreground">In selected date range</p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Orders</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{filteredSummary.totalOrders.toLocaleString()}</div>
                <p className="text-xs text-muted-foreground">In selected date range</p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Quantity</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{filteredSummary.totalQuantity.toLocaleString()}</div>
                <p className="text-xs text-muted-foreground">In selected date range</p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Products</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{filteredSummary.uniqueProducts}</div>
                <p className="text-xs text-muted-foreground">In selected date range</p>
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {/* Other Revenue (Packaging + Delivery Fees) */}
      <div className="space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2 flex-wrap">
            <h2 className="text-lg font-semibold">Other Revenue (Fees)</h2>
            <Badge variant="outline">
              {feesSummary?.days ?? 0} day{(feesSummary?.days ?? 0) === 1 ? '' : 's'} in current filter
            </Badge>
            {feesCoverage && (
              <Badge variant="secondary" title={`Last synced: ${format(new Date(feesCoverage.lastSyncedAt), 'PPpp')}`}>
                DB coverage: {format(new Date(feesCoverage.minDate), 'MMM dd, yyyy')} – {format(new Date(feesCoverage.maxDate), 'MMM dd, yyyy')} ({feesCoverage.totalDays} days)
              </Badge>
            )}
          </div>
        </div>

        {/* Dedicated fees sync date range */}
        <Card>
          <CardContent className="pt-4">
            <div className="flex flex-wrap items-end gap-3">
              <div className="space-y-1">
                <label className="text-xs font-medium text-muted-foreground">Fees Sync From</label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" size="sm" className="w-44 justify-start font-normal">
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {format(feesStartDate, 'MMM dd, yyyy')}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar mode="single" selected={feesStartDate} onSelect={(d) => d && setFeesStartDate(d)} initialFocus />
                  </PopoverContent>
                </Popover>
              </div>
              <div className="space-y-1">
                <label className="text-xs font-medium text-muted-foreground">Fees Sync To</label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" size="sm" className="w-44 justify-start font-normal">
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {format(feesEndDate, 'MMM dd, yyyy')}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar mode="single" selected={feesEndDate} onSelect={(d) => d && setFeesEndDate(d)} initialFocus />
                  </PopoverContent>
                </Popover>
              </div>
              <Button onClick={handleSyncFees} size="sm" disabled={isFeesSyncing}>
                <RefreshCw className={cn('mr-2 h-4 w-4', isFeesSyncing && 'animate-spin')} />
                {isFeesSyncing ? 'Syncing Fees...' : 'Sync Fees for Selected Range'}
              </Button>
              <p className="text-xs text-muted-foreground ml-auto">
                The cards below reflect the page's main date filter, not this sync range.
              </p>
            </div>
          </CardContent>
        </Card>

        {feesSummary ? (
          <div className="grid gap-4 md:grid-cols-3">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Packaging Fee</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{feesSummary.packaging.toLocaleString(undefined, { maximumFractionDigits: 2 })}</div>
                <p className="text-xs text-muted-foreground">Total packaging fees in range</p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Delivery Charge</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{feesSummary.delivery.toLocaleString(undefined, { maximumFractionDigits: 2 })}</div>
                <p className="text-xs text-muted-foreground">Total delivery charges in range</p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Combined Fees</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{feesSummary.combined.toLocaleString(undefined, { maximumFractionDigits: 2 })}</div>
                <p className="text-xs text-muted-foreground">Packaging + Delivery</p>
              </CardContent>
            </Card>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">No fees data for this range yet. Click "Sync Fees for Range" to fetch.</p>
        )}
      </div>

      {/* Accepted Daily Totals (orders / quantity / GMV from accepted_daily) */}
      <div id="accepted-daily" className="space-y-4 scroll-mt-24">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2 flex-wrap">
            <h2 className="text-lg font-semibold">Daily Accepted Orders (Orders / Quantity / GMV)</h2>
            <Badge variant="outline">
              {acceptedSummary?.days ?? 0} day{(acceptedSummary?.days ?? 0) === 1 ? '' : 's'} in current filter
            </Badge>
            {acceptedCoverage && (
              <Badge variant="secondary" title={`Last synced: ${format(new Date(acceptedCoverage.lastSyncedAt), 'PPpp')}`}>
                DB coverage: {format(new Date(acceptedCoverage.minDate), 'MMM dd, yyyy')} – {format(new Date(acceptedCoverage.maxDate), 'MMM dd, yyyy')} ({acceptedCoverage.totalDays} days)
              </Badge>
            )}
          </div>
        </div>

        <Card>
          <CardContent className="pt-4">
            <div className="flex flex-wrap items-end gap-3">
              <div className="space-y-1">
                <label className="text-xs font-medium text-muted-foreground">Accepted Sync From</label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" size="sm" className="w-44 justify-start font-normal">
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {format(acceptedStartDate, 'MMM dd, yyyy')}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar mode="single" selected={acceptedStartDate} onSelect={(d) => d && setAcceptedStartDate(d)} initialFocus />
                  </PopoverContent>
                </Popover>
              </div>
              <div className="space-y-1">
                <label className="text-xs font-medium text-muted-foreground">Accepted Sync To</label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" size="sm" className="w-44 justify-start font-normal">
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {format(acceptedEndDate, 'MMM dd, yyyy')}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar mode="single" selected={acceptedEndDate} onSelect={(d) => d && setAcceptedEndDate(d)} initialFocus />
                  </PopoverContent>
                </Popover>
              </div>
              <Button onClick={handleSyncAccepted} size="sm" disabled={isAcceptedSyncing}>
                <RefreshCw className={cn('mr-2 h-4 w-4', isAcceptedSyncing && 'animate-spin')} />
                {isAcceptedSyncing ? 'Syncing...' : 'Sync Accepted Totals for Range'}
              </Button>
              <p className="text-xs text-muted-foreground ml-auto">
                Order-level data (excludes REJECTED_BY_VENDOR), bucketed by order day (Africa/Nairobi).
              </p>
            </div>
          </CardContent>
        </Card>

        {acceptedSummary && acceptedSummary.days > 0 ? (
          <>
            <div className="grid gap-4 md:grid-cols-3">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Total Orders</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{acceptedSummary.orders.toLocaleString()}</div>
                  <p className="text-xs text-muted-foreground">In current page filter</p>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Total Quantity</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{acceptedSummary.quantity.toLocaleString(undefined, { maximumFractionDigits: 2 })}</div>
                  <p className="text-xs text-muted-foreground">In current page filter</p>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Total GMV</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{acceptedSummary.gmv.toLocaleString(undefined, { maximumFractionDigits: 2 })}</div>
                  <p className="text-xs text-muted-foreground">Price × Quantity</p>
                </CardContent>
              </Card>
            </div>

            <Card>
              <CardContent className="pt-4">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead className="text-right">Orders</TableHead>
                      <TableHead className="text-right">Quantity</TableHead>
                      <TableHead className="text-right">GMV</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {acceptedRows!.map((r) => (
                      <TableRow key={r.date}>
                        <TableCell>{format(new Date(r.date), 'MMM dd, yyyy')}</TableCell>
                        <TableCell className="text-right">{Number(r.total_orders).toLocaleString()}</TableCell>
                        <TableCell className="text-right">{Number(r.total_quantity).toLocaleString(undefined, { maximumFractionDigits: 2 })}</TableCell>
                        <TableCell className="text-right">{Number(r.total_gmv).toLocaleString(undefined, { maximumFractionDigits: 2 })}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </>
        ) : (
          <p className="text-sm text-muted-foreground">No accepted-daily data for this range yet. Click "Sync Accepted Totals for Range" to fetch.</p>
        )}
      </div>



      {/* Product Filter and Chart Visualization */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold">Sales Trends Visualization</h2>
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-muted-foreground">
              Filter by Product:
            </label>
            <Select value={selectedProduct} onValueChange={setSelectedProduct}>
              <SelectTrigger className="w-64">
                <SelectValue placeholder="All Products" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Products</SelectItem>
                {availableProducts.map((product) => (
                  <SelectItem key={product} value={product}>
                    {product}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        
        <ChipChipSalesChart 
          data={salesData || []} 
          selectedProduct={selectedProduct === 'all' ? undefined : selectedProduct}
        />
      </div>

      {/* Sync Controls */}
      <Card>
        <CardHeader>
          <CardTitle>Data Synchronization</CardTitle>
          <CardDescription>
            Sync sales data from ChipChip platform to local database
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Date Range Picker */}
          <div className="flex gap-4 items-center">
            <div className="flex gap-2">
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-[240px] justify-start text-left font-normal",
                      !startDate && "text-muted-foreground"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {startDate ? format(startDate, "PPP") : "Start date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={startDate}
                    onSelect={(date) => date && setStartDate(date)}
                    initialFocus
                    className="pointer-events-auto"
                  />
                </PopoverContent>
              </Popover>

              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-[240px] justify-start text-left font-normal",
                      !endDate && "text-muted-foreground"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {endDate ? format(endDate, "PPP") : "End date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={endDate}
                    onSelect={(date) => date && setEndDate(date)}
                    initialFocus
                    className="pointer-events-auto"
                  />
                </PopoverContent>
              </Popover>
            </div>

            <Button 
              onClick={handleSync} 
              disabled={isSyncing}
              className="flex items-center gap-2"
            >
              <RefreshCw className={cn("h-4 w-4", isSyncing && "animate-spin")} />
              Sync Date Range
            </Button>

            <Button 
              onClick={handleFullSync} 
              disabled={isSyncing}
              variant="outline"
              className="flex items-center gap-2"
            >
              <RefreshCw className={cn("h-4 w-4", isSyncing && "animate-spin")} />
              Full Sync (May 2024 - Now)
            </Button>
          </div>

          {/* Progress Bar */}
          {isSyncing && (
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">{syncStatus}</span>
                <span className="text-muted-foreground">{syncProgress}%</span>
              </div>
              <Progress value={syncProgress} className="w-full" />
            </div>
          )}
        </CardContent>
      </Card>

      {/* Data Table */}
      <Card>
        <CardHeader>
          <CardTitle>Sales Data</CardTitle>
          <CardDescription>
            ChipChip sales data for the selected date range
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isDataLoading ? (
            <div className="flex items-center justify-center py-8">
              <RefreshCw className="h-8 w-8 animate-spin" />
            </div>
          ) : salesData?.length ? (
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Product Name</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Order Type</TableHead>
                    <TableHead className="text-right">Price (ETB)</TableHead>
                    <TableHead className="text-right">Orders</TableHead>
                    <TableHead className="text-right">Quantity</TableHead>
                    <TableHead>Sync Date</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {salesData.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell className="font-medium">{item.product_name}</TableCell>
                      <TableCell>{format(new Date(item.date), 'MMM dd, yyyy')}</TableCell>
                      <TableCell>
                        <Badge variant="outline">{item.order_type}</Badge>
                      </TableCell>
                      <TableCell className="text-right">{item.price.toLocaleString()}</TableCell>
                      <TableCell className="text-right">{item.num_order.toLocaleString()}</TableCell>
                      <TableCell className="text-right">{item.quantity.toLocaleString()}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {format(new Date(item.sync_date), 'MMM dd, yyyy HH:mm')}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              No sales data found for the selected date range. Try syncing data first.
            </div>
          )}
        </CardContent>
      </Card>
    </div>
    </Layout>
  );
};

export default ChipChipSalesData;